/**
 * historyFloor — hides a gym's ascent history from before a non-exempt
 * viewer joined. "What happened before I was here has nothing to do with
 * me" — the viewer's own data is never limited by this.
 *
 * Pure helper: the caller resolves `exempt` (gymAdmin's `stats.viewAdvanced`
 * cap, or the platform `platform.gym.adminAny` cap) and passes it in, and
 * supplies the member doc + gym timezone. See docs/spec/12-user-app.md
 * "Hiding the history from before joining".
 */

/**
 * @param {object} params
 * @param {object|null|undefined} params.member - the viewer's OWN member doc
 *   for this gym (`gyms/{gymId}/members/{uid}`). Only `joinedAt` is read.
 * @param {string} [params.timezone] - the gym's IANA timezone
 *   (`gymMeta.timezone`). Falls back to browser-local when missing/invalid.
 * @param {boolean} [params.exempt] - true skips the floor entirely (admin
 *   surfaces, gymAdmin, platform.gym.adminAny).
 * @returns {string|null} "YYYY-MM-DD" floor date, or null when unrestricted.
 */
export function historyFloor({ member, timezone, exempt } = {}) {
  if (exempt) return null;
  const joinedAt = member?.joinedAt;
  if (joinedAt == null) return null;
  const d = toJsDate(joinedAt);
  if (!d || isNaN(d.getTime())) return null;
  return floorDateKey(d, timezone);
}

/**
 * Clamp a { from, to } "YYYY-MM-DD" range to a floor date.
 * `from` is raised to the floor when it is earlier (or unbounded/null);
 * if the range ends before the floor, it is reported empty (`to` is left
 * as-is — callers that need a display range keep the original end).
 *
 * @param {{from: string|null, to: string|null}} range
 * @param {string|null} floor - "YYYY-MM-DD", or null/undefined for no clamp
 * @returns {{from: string|null, to: string|null, empty: boolean}}
 */
export function clampRange({ from, to } = {}, floor) {
  if (!floor) return { from, to, empty: false };
  const clampedFrom = from && from > floor ? from : floor;
  if (to && to < floor) return { from: clampedFrom, to, empty: true };
  return { from: clampedFrom, to, empty: false };
}

/** Convert a Firestore Timestamp / Date / {seconds} / epoch-ms into a Date. */
function toJsDate(v) {
  if (v instanceof Date) return v;
  if (typeof v === "object") {
    if (typeof v.toDate === "function") {
      try { return v.toDate(); } catch { return null; }
    }
    if (typeof v.seconds === "number") {
      return new Date(v.seconds * 1000 + Math.round((v.nanoseconds || 0) / 1e6));
    }
  }
  if (typeof v === "number") return new Date(v);
  return null;
}

/**
 * Format a Date as "YYYY-MM-DD" in the given IANA timezone, using the same
 * `formatToParts` approach as `todayStr` in `../utils` (numeric parts, never
 * a locale-formatted string, so the result is never "M/D/YYYY"-shaped).
 * Falls back to browser-local on a missing/invalid timezone.
 */
function floorDateKey(date, timezone) {
  const local = () => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  if (timezone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(date);
      const pick = (type) => parts.find(p => p.type === type)?.value;
      const s = `${pick("year")}-${pick("month")}-${pick("day")}`;
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    } catch { /* invalid timezone — use local fallback below */ }
  }
  return local();
}
